/**
 * Local Australian ringback for an outbound INVITE that got a 180 without SDP: 400 Hz + 425 Hz,
 * cadence 0.4 s on, 0.2 s off, 0.4 s on, 2.0 s off (AS/CA S002). Stops on 183/200/failure.
 */
export interface RingbackSink {
  /** called with (on) at each cadence edge; the audio engine turns the tone pair on/off */
  setTone(on: boolean): void;
}

export const AU_RINGBACK_HZ = [400, 425] as const;
export const AU_RINGBACK_CADENCE_MS = [400, 200, 400, 2000] as const;

export class Ringback {
  private step = 0;
  private handle: ReturnType<typeof setTimeout> | null = null;
  private running = false;

  constructor(
    private readonly sink: RingbackSink,
    private readonly setT: (fn: () => void, ms: number) => ReturnType<typeof setTimeout> = (fn, ms) => setTimeout(fn, ms),
    private readonly clearT: (h: ReturnType<typeof setTimeout>) => void = (h) => clearTimeout(h),
  ) {}

  get isRunning(): boolean {
    return this.running;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.step = 0;
    this.tick();
  }

  stop(): void {
    this.running = false;
    if (this.handle !== null) this.clearT(this.handle);
    this.handle = null;
    this.sink.setTone(false);
  }

  private tick(): void {
    if (!this.running) return;
    const on = this.step % 2 === 0;
    this.sink.setTone(on);
    const ms = AU_RINGBACK_CADENCE_MS[this.step % AU_RINGBACK_CADENCE_MS.length] ?? 1000;
    this.step = (this.step + 1) % AU_RINGBACK_CADENCE_MS.length;
    this.handle = this.setT(() => this.tick(), ms);
  }
}
